import React, { useState, useEffect } from 'react'

import i18n from 'live/services/i18n'

import { RAListHeader } from './Header/RAListHeader'
import { RAListTable } from './Table/RAListTable'
import { useReportsApproval } from '../../hooks/useReportsApproval'
import { ReportsApprovalTypes } from '../../types/ReportsApproval'
import { ReportsApprovalColumns, SortOption } from './Table/helper/helper'
import {
    CancelCustomButton,
    ConfirmCustomButton,
    LiveModalWrapper,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle
} from '../../utils/liveModalCustom'
import { getDashboardsFromLive, updateSchedulerDataCheck, updateSchedulerSendCheck } from '../../api/api'
import { formatAssetNameByRegex, mountFullDateAccordingToLocale } from '../../utils/utils'
import { DateCheckResponseTypes, SendCheckResponseTypes } from '../../types/ApiResponses'

export function ReportsApprovalList() {
    const [reportsApproval] = useReportsApproval()
    const [searchTxt, setSearchText] = useState('')
    const [filteredReportsApproval, setFilteredReportsApproval] = useState<ReportsApprovalTypes[]>(reportsApproval)
    const [reportsLimit, setReportsLimit] = useState(50)
    const [selectedSorter, setSelectedSorter] = useState<SortOption>({
        field: ReportsApprovalColumns.ID,
        reverse: true
    })
    const [checkModalOpen, setCheckModalOpen] = useState({ modalType: '', isOpen: false })
    const [currentSchedulerId, setCurrentSchedulerId] = useState<number | null>(null)
    const isCheckDataModalOpen = checkModalOpen.modalType === 'data'

    function onChangeSearch(text: string) {
        const newReportsApproval = reportsApproval.filter(
            report =>
                report?.reportname?.toLowerCase().includes(text.toLowerCase()) ||
                report?.schedulename?.toLowerCase().includes(text.toLowerCase()) ||
                report?.alarmname?.toLowerCase().includes(text.toLowerCase()) ||
                report?.asset?.toLowerCase().includes(text.toLowerCase()) ||
                report?.timespan?.toLowerCase().includes(text.toLowerCase()) ||
                report?.usernamecheckdata?.toLowerCase().includes(text.toLowerCase()) ||
                report?.usernamesenddata?.toLowerCase().includes(text.toLowerCase())
        )

        setFilteredReportsApproval(newReportsApproval)
        setSearchText(text)
    }

    function openStaticDataDashboard(scheduler: ReportsApprovalTypes, dashboardId: number) {
        // This regex identifies the last two parentheses in assets and removes them, leaving only the asset name. E.g. from well 2 (Well) (2) to well 2
        const regex = /\s\((\w+)\)\s\((\w+)\)/

        const formattedDate = mountFullDateAccordingToLocale(scheduler.timespan, true)

        const assetName = formatAssetNameByRegex(scheduler.asset, regex)
        const STATIC_DATA_URL = `/dashboard/${dashboardId}#/?mode=view&span=${formattedDate}&filter=%257B%2522asset_name_event_type%2522%253A%255B%2522${assetName}%2522%255D%257D`
        window.open(STATIC_DATA_URL, '_blank')
    }

    function updateSchedulerCheckStatus(
        response: DateCheckResponseTypes | SendCheckResponseTypes,
        currentSchedulerList: ReportsApprovalTypes[],
        schedulerId: number,
        dashboardId?: number
    ): ReportsApprovalTypes[] {
        const checkStatusUpdated = currentSchedulerList.map(scheduler => {
            if (scheduler.id === schedulerId) {
                if (isCheckDataModalOpen) {
                    const dateCheckResponse = response as DateCheckResponseTypes
                    if (!dashboardId) {
                        return {
                            ...scheduler,
                            datastatus: 'DASHBOARD_NOT_FOUND',
                            lastdatecheckdata: dateCheckResponse.lastDateCheckData,
                            usernamecheckdata: dateCheckResponse.userNameCheckData
                        }
                    } else {
                        openStaticDataDashboard(scheduler, dashboardId)

                        return {
                            ...scheduler,
                            datastatus: dateCheckResponse.checkStatus,
                            lastdatecheckdata: dateCheckResponse.lastDateCheckData,
                            usernamecheckdata: dateCheckResponse.userNameCheckData
                        }
                    }
                } else {
                    const sendCheckResponse = response as SendCheckResponseTypes

                    return {
                        ...scheduler,
                        sendstatus: sendCheckResponse.sendStatus,
                        lastdatesenddata: sendCheckResponse.lastDateSendData,
                        usernamesenddata: sendCheckResponse.userNameSendData
                    }
                }
            }

            return { ...scheduler }
        })

        return checkStatusUpdated
    }

    async function updateDataCheckAndOpenStaticDataDashboard(schedulerId: number) {
        const currentSchedulerList = [...filteredReportsApproval]

        const response = await updateSchedulerDataCheck(schedulerId)
        const dashboards = await getDashboardsFromLive()

        const dashboardId = dashboards.find(dashboard => dashboard.title === '[KPI] Static Data')?.id

        if (response) {
            const schedulerCheckStatusUpdated = updateSchedulerCheckStatus(
                response,
                currentSchedulerList,
                schedulerId,
                dashboardId
            )
            setFilteredReportsApproval(schedulerCheckStatusUpdated)
        }
    }

    async function sendReportAndUpdateSendCheck(schedulerId: number) {
        const currentSchedulerList = [...filteredReportsApproval]

        const response = await updateSchedulerSendCheck(schedulerId)

        if (response) {
            const schedulerCheckStatusUpdated = updateSchedulerCheckStatus(response, currentSchedulerList, schedulerId)
            setFilteredReportsApproval(schedulerCheckStatusUpdated)
        }
    }

    function openCheckModalAccordingToType(buttonType: 'data' | 'send', schedulerId: number) {
        setCheckModalOpen({ modalType: buttonType !== 'data' ? 'send' : 'data', isOpen: true })
        setCurrentSchedulerId(schedulerId)
    }

    function handleConfirmButtonCheckModal() {
        isCheckDataModalOpen
            ? updateDataCheckAndOpenStaticDataDashboard(currentSchedulerId)
            : sendReportAndUpdateSendCheck(currentSchedulerId)

        closeModal()
    }

    function closeModal() {
        setCheckModalOpen({ modalType: '', isOpen: false })
    }

    useEffect(() => {
        setFilteredReportsApproval(reportsApproval.sort((a, b) => b.id - a.id).slice(0, reportsLimit))
    }, [reportsApproval, reportsLimit])

    return (
        <>
            <RAListHeader
                searchTxt={searchTxt}
                onChangeSearch={onChangeSearch}
                selectedSorter={selectedSorter}
                setSelectedSorter={setSelectedSorter}
            />
            <RAListTable
                reportsApproval={reportsApproval}
                filteredReportsApproval={filteredReportsApproval}
                selectedSorter={selectedSorter}
                setSelectedSorter={setSelectedSorter}
                reportsLimit={reportsLimit}
                setReportsLimit={setReportsLimit}
                openCheckModalAccordingToType={openCheckModalAccordingToType}
            />
            <LiveModalWrapper show={checkModalOpen.isOpen}>
                <ModalHeader>
                    <ModalTitle>
                        <>
                            <i className="fas fa-exclamation-circle"></i>
                            <span>
                                {isCheckDataModalOpen
                                    ? i18n('approvalListCheckDataTitle')
                                    : i18n('approvalListSendDataTitle')}
                            </span>
                        </>
                    </ModalTitle>
                </ModalHeader>
                <ModalBody>
                    <>
                        <div>
                            {isCheckDataModalOpen
                                ? i18n('approvalListCheckDataMessage')
                                : i18n('approvalListSendDataMessage')}
                        </div>
                        <div style={{ marginTop: '5px' }}>
                            <b>{i18n('approvalListUndoneAlertMessage')}</b>
                        </div>
                    </>
                </ModalBody>
                <ModalFooter>
                    <ConfirmCustomButton onClick={handleConfirmButtonCheckModal}>
                        {isCheckDataModalOpen ? i18n('confirmCheckDataButton') : i18n('confirmSendDataButton')}
                    </ConfirmCustomButton>
                    <CancelCustomButton onClick={closeModal}>{i18n('Cancel')}</CancelCustomButton>
                </ModalFooter>
            </LiveModalWrapper>
        </>
    )
}
